/**
 * Typed admin-API client.
 *
 * Mirrors the FastAPI Pydantic schemas in
 *   hooten-young-sales/src/hy_sales/schemas/admin.py
 */

import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { RolePublic, UserDetail } from './auth';

// =====================================================================
// Types — keep in lockstep with hy_sales/schemas/admin.py
// =====================================================================

export type UserSetupStatus = 'completed' | 'pending_setup' | 'link_expired';

export interface UserListItem {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: 'pending' | 'active' | 'rejected' | 'disabled';
  /** Whether the user has finished claiming their account.  Computed
   *  by the backend from `last_login_at` + the most recent unused
   *  `set_password` token.  Drives the row chip and the visibility
   *  of the "Resend invite" action in the kebab menu. */
  setup_status: UserSetupStatus;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
  roles: RolePublic[];
}

export interface UserListResponse {
  items: UserListItem[];
  total: number;
  limit: number;
  offset: number;
}

export type UserSortKey = 'name' | 'email' | 'status' | 'last_sign_in' | 'created';
export type SortDir = 'asc' | 'desc';

export interface UserListFilters {
  limit?: number;
  offset?: number;
  status?: UserListItem['status'];
  /** Filter by computed claim state: completed / pending_setup /
   *  link_expired.  Drives the setup-pipeline KPIs on the admin
   *  overview.  See backend _compute_setup_status for semantics. */
  setup_status?: UserSetupStatus;
  role?: string;
  search?: string;
  sort_by?: UserSortKey;
  sort_dir?: SortDir;
}

export interface AdminCreateUserRequest {
  email: string;
  first_name: string;
  last_name: string;
  role_ids: string[];
}

export interface AdminCreateUserResponse {
  user: UserDetail;
  set_password_url: string | null;
  expires_at: string;
}

export interface AdminIssueResetResponse {
  user: UserDetail;
  reset_url: string | null;
  expires_at: string;
  purpose: 'forgot_password' | 'set_password';
}

export interface AdminUpdateUserRolesRequest {
  role_ids: string[];
}

export interface AdminUpdateUserProfileRequest {
  first_name: string;
  last_name: string;
}

export interface AdminUpdateUserStatusRequest {
  status: UserListItem['status'];
}

export interface RoleWithUsage {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  is_system: boolean;
  user_count: number;
}

export interface RoleListResponse {
  items: RoleWithUsage[];
}

export interface AdminCreateRoleRequest {
  name: string;
  display_name: string;
  description?: string | null;
}

export interface AuditLogEntry {
  id: number;
  user_id: string | null;
  user_email: string | null;
  action: string;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  occurred_at: string;
}

export interface AuditLogResponse {
  items: AuditLogEntry[];
  next_cursor: number | null;
}

export type AuditCategory =
  | 'signins'
  | 'signups'
  | 'passwords'
  | 'roles'
  | 'admin_actions'
  | 'data_uploads'
  | 'security';

export interface AuditLogFilters {
  limit?: number;
  cursor?: number;
  action?: string;
  category?: AuditCategory;
  user_id?: string;
  since?: string;
  until?: string;
}

// =====================================================================
// Endpoint wrappers
// =====================================================================

export function fetchUsers(filters: UserListFilters = {}): Promise<UserListResponse> {
  return apiGet<UserListResponse>('sales', '/api/admin/users', { ...filters });
}

export function createUser(payload: AdminCreateUserRequest): Promise<AdminCreateUserResponse> {
  return apiPost<AdminCreateUserResponse>('sales', '/api/admin/users', payload);
}

export function updateUserRoles(
  userId: string,
  payload: AdminUpdateUserRolesRequest,
): Promise<UserDetail> {
  return apiPatch<UserDetail>('sales', `/api/admin/users/${userId}/roles`, payload);
}

export function updateUserStatus(
  userId: string,
  payload: AdminUpdateUserStatusRequest,
): Promise<UserDetail> {
  return apiPatch<UserDetail>('sales', `/api/admin/users/${userId}/status`, payload);
}

export function updateUserProfile(
  userId: string,
  payload: AdminUpdateUserProfileRequest,
): Promise<UserDetail> {
  return apiPatch<UserDetail>('sales', `/api/admin/users/${userId}/profile`, payload);
}

export function resetUserPassword(userId: string): Promise<AdminIssueResetResponse> {
  return apiPost<AdminIssueResetResponse>('sales', `/api/admin/users/${userId}/reset-password`);
}

export function resendUserInvitation(userId: string): Promise<AdminIssueResetResponse> {
  return apiPost<AdminIssueResetResponse>('sales', `/api/admin/users/${userId}/resend-invitation`);
}

/** Soft-delete a user.  The row stays in the DB (preserves audit log
 *  + visit-note attribution) but is marked status='deleted', has its
 *  email mutated to a sentinel value so the original slot frees up
 *  for a fresh admin-create with the same address, and has all role
 *  grants + pending reset tokens cleared.  204 No Content on success.
 *  Backend safety rails: can't delete self, can't delete the last
 *  active admin. */
export function deleteUser(userId: string): Promise<void> {
  return apiDelete<void>('sales', `/api/admin/users/${userId}`);
}

export function fetchRoles(): Promise<RoleListResponse> {
  return apiGet<RoleListResponse>('sales', '/api/admin/roles');
}

export function createRole(payload: AdminCreateRoleRequest): Promise<RoleWithUsage> {
  return apiPost<RoleWithUsage>('sales', '/api/admin/roles', payload);
}

export function fetchAuditLog(filters: AuditLogFilters = {}): Promise<AuditLogResponse> {
  return apiGet<AuditLogResponse>('sales', '/api/admin/audit-log', { ...filters });
}
